import React, { useState, useEffect } from "react";
import "./NewPatientRegistration.css";

const theme = {
  primaryDark: "#1b325f", primaryBlue: "#282f81", accentCyan: "#11abea",
  bgLight: "#f8fafc", cardBg: "#ffffff", textMain: "#334155",
  textMuted: "#64748b", borderLight: "#e2e8f0"
};

const IpdProcedures = ({ screenAction, triggerSave }) => {
  const [isDisabled, setIsDisabled] = useState(true);
  const [currentMode, setCurrentMode] = useState("add");
  
  // Search States
  const [admissionSearch, setAdmissionSearch] = useState("");
  const [admissionResults, setAdmissionResults] = useState([]);
  const [showAdmissionDropdown, setShowAdmissionDropdown] = useState(false);

  const [doctorSearch, setDoctorSearch] = useState("");
  const [doctorResults, setDoctorResults] = useState([]);
  const [showDoctorDropdown, setShowDoctorDropdown] = useState(false);
  const [selectedDoctor, setSelectedDoctor] = useState(null);

  const [procedureSearch, setProcedureSearch] = useState("");
  const [procedureResults, setProcedureResults] = useState([]);
  const [showProcedureDropdown, setShowProcedureDropdown] = useState(false);
  const [selectedProcedure, setSelectedProcedure] = useState(null);

  const [formData, setFormData] = useState({
    admission_id: "",
    patient_id: "",
    patient_name: "",
    department: "",
    ward_type: "",
    room_no: "",
    bed_no: "",
    age: "",
    gender: "",
    procedures_array: []
  });

  const [currentProc, setCurrentProc] = useState({
    surgery_date: new Date().toISOString().slice(0, 16),
    proc_id: "",
    proc_name: "",
    location: "",
    duration: "",
    priority: "Routine",
    notes: "",
    status: "Scheduled"
  });

  // --- ADMISSION SEARCH ---
  const handleAdmissionSearch = async (val) => {
    setAdmissionSearch(val);
    if (val.length < 3) {
      setAdmissionResults([]);
      setShowAdmissionDropdown(false);
      return;
    }
    try {
      const user = JSON.parse(localStorage.getItem("user"));
      const res = await fetch(`https://hmsapi.emedhatechnologies.com/master/api/api/admission-vitals/search?q=${val}&employee_id=${user?.id}`);
      const data = await res.json();
      setAdmissionResults(data);
      setShowAdmissionDropdown(true);
    } catch (err) { console.error(err); }
  };

  const loadExistingProcedures = async (admissionId) => {
    try {
      const res = await fetch(`https://hmsapi.emedhatechnologies.com/master/api/api/ipd-procedures/${admissionId}`);
      if (res.ok) {
        const data = await res.json();
        setFormData(prev => ({ ...prev, procedures_array: data }));
      }
    } catch (err) { console.error("Failed to load existing procedures"); }
  };

  const selectAdmission = (adm) => {
    setFormData(prev => ({
      ...prev,
      admission_id: adm.admission_id,
      patient_id: adm.patient_id,
      patient_name: adm.patient_name,
      department: adm.department,
      ward_type: adm.ward_type,
      room_no: adm.room_no,
      bed_no: adm.bed_no,
      age: adm.age,
      gender: adm.gender
    }));
    setAdmissionSearch(adm.admission_id);
    setShowAdmissionDropdown(false);
    
    if (screenAction === "edit" || screenAction === "view") {
        loadExistingProcedures(adm.admission_id);
    }
    
    if (screenAction === "edit" || screenAction === "add") setIsDisabled(false);
    if (screenAction === "view") setIsDisabled(true);
  };

  // --- SURGEON / DOCTOR SEARCH ---
  const handleDoctorSearch = async (val) => {
    setDoctorSearch(val);
    if (val.length < 3) {
      setDoctorResults([]);
      setShowDoctorDropdown(false);
      return;
    }
    try {
      const user = JSON.parse(localStorage.getItem("user"));
      const res = await fetch(`https://hmsapi.emedhatechnologies.com/master/api/api/ipd/doctors/search?q=${val}&employee_id=${user?.id}`);
      const data = await res.json();
      setDoctorResults(data);
      setShowDoctorDropdown(true);
    } catch (err) { console.error(err); }
  };

  const selectDoctor = (doc) => {
    setSelectedDoctor(doc);
    setDoctorSearch(doc.doctor_name);
    setShowDoctorDropdown(false);
  };

  // --- PROCEDURE MASTER SEARCH ---
  const handleProcedureSearch = async (val) => {
    setProcedureSearch(val);
    if (val.length < 2) {
      setProcedureResults([]);
      setShowProcedureDropdown(false);
      return;
    }
    try {
      const user = JSON.parse(localStorage.getItem("user"));
      const res = await fetch(`https://hmsapi.emedhatechnologies.com/master/api/api/ipd/procedures/search?q=${val}&employee_id=${user?.id}`);
      const data = await res.json();
      setProcedureResults(data);
      setShowProcedureDropdown(true);
    } catch (err) { console.error(err); }
  };

  const selectProcedure = (proc) => {
    setSelectedProcedure(proc);
    setProcedureSearch(proc.proc_name);
    setCurrentProc({ 
      ...currentProc, 
      proc_id: proc.proc_id, 
      proc_name: proc.proc_name, 
      location: proc.surgery_location || "", 
      duration: proc.duration || "" 
    });
    setShowProcedureDropdown(false);
  };

  // --- ARRAY MANAGEMENT ---
  const addProcedure = () => {
    if (!selectedProcedure) return alert("Please search and select a Procedure.");
    if (!selectedDoctor) return alert("Please select the Surgeon / Performing Doctor.");

    setFormData(prev => ({
      ...prev,
      procedures_array: [{
          ...currentProc,
          performed_by_cd: selectedDoctor.doc_cd,
          performed_by_name: selectedDoctor.doctor_name
      }, ...prev.procedures_array]
    }));

    setCurrentProc({
      surgery_date: new Date().toISOString().slice(0, 16),
      proc_id: "",
      proc_name: "",
      location: "",
      duration: "",
      priority: "Routine",
      notes: "",
      status: "Scheduled"
    });
    setProcedureSearch("");
    setSelectedProcedure(null);
  };

  const removeProcedure = (indexToRemove) => {
    setFormData(prev => ({
      ...prev,
      procedures_array: prev.procedures_array.filter((_, idx) => idx !== indexToRemove)
    }));
  };

  // --- ACTIONS ---
  const clearForm = () => {
    setFormData({ admission_id: "", patient_id: "", patient_name: "", department: "", ward_type: "", room_no: "", bed_no: "", age: "", gender: "", procedures_array: [] });
    setAdmissionSearch("");
    setDoctorSearch("");
    setProcedureSearch("");
    setSelectedDoctor(null);
    setSelectedProcedure(null);
    setIsDisabled(screenAction === "view");
  };

  useEffect(() => {
    if (triggerSave > 0 && screenAction === "save") handleSubmit();
  }, [triggerSave]);

  useEffect(() => {
    if (screenAction === "add") { setCurrentMode("add"); clearForm(); setIsDisabled(false); }
    if (screenAction === "edit") { setCurrentMode("edit"); setIsDisabled(false); }
    if (screenAction === "view") { setCurrentMode("view"); setIsDisabled(true); }
    if (screenAction === "refresh") clearForm();
    if (screenAction === "print") printForm();
  }, [screenAction]);

  const handleSubmit = async () => {
    if (!formData.admission_id) return alert("Please select an Admission.");
    if (formData.procedures_array.length === 0) return alert("No procedures to save.");

    const user = JSON.parse(localStorage.getItem("user"));
    const payload = {
      admission_id: formData.admission_id,
      created_by: user.id,
      procedures: formData.procedures_array
    };

    const method = currentMode === "edit" ? "PUT" : "POST";
    const url = currentMode === "edit"
      ? `https://hmsapi.emedhatechnologies.com/master/api/api/ipd-procedures/${formData.admission_id}`
      : `https://hmsapi.emedhatechnologies.com/master/api/api/ipd-procedures`;

    try {
      const res = await fetch(url, {
        method: method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      });
      
      if (res.ok) {
        alert(currentMode === "edit" ? "Procedures Updated Successfully!" : "Procedures Scheduled Successfully!");
        if (currentMode === "add") clearForm();
      } else {
        alert("Error saving procedures.");
      }
    } catch (err) {
      alert("Network error.");
    }
  };

  const printForm = () => {
    if (!formData.admission_id) return alert("Please select an admission to print.");
    const newWindow = window.open("", "", "width=1000,height=800");
    const today = new Date().toLocaleDateString();

    const htmlContent = `
      <html>
        <head>
          <title>Surgery Schedule - ${formData.admission_id}</title>
          <style>
            body { font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; color: #333; margin: 0; padding: 40px; }
            .header { display: flex; justify-content: space-between; align-items: center; border-bottom: 2px solid #282f81; padding-bottom: 10px; margin-bottom: 20px; }
            .hospital-info h1 { margin: 0; color: #1b325f; font-size: 24px; text-transform: uppercase; }
            .hospital-info p { margin: 2px 0; font-size: 12px; color: #64748b; }
            .section-header { background: #f8fafc; padding: 8px 12px; font-weight: bold; font-size: 14px; border-left: 4px solid #282f81; margin-top: 20px; margin-bottom: 10px; text-transform: uppercase; }
            .data-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 10px; margin-bottom: 20px; }
            .data-item { font-size: 13px; border-bottom: 1px dotted #ccc; padding: 6px 0; }
            .label { font-weight: 600; color: #555; display: inline-block; width: 140px; }
            table { width: 100%; border-collapse: collapse; margin-top: 15px; font-size: 13px; }
            th, td { border: 1px solid #ccc; padding: 8px; text-align: left; vertical-align: top; }
            th { background-color: #f4f0fa; }
            .footer { margin-top: 50px; font-size: 10px; text-align: center; color: #95a5a6; border-top: 1px solid #eee; padding-top: 10px; }
          </style>
        </head>
        <body onload="window.print(); window.close();">
          <div class="header">
            <div class="hospital-info">
              <h1>EMEDHA CARE HOSPITAL</h1>
              <p>Surgical & Procedures Department</p>
            </div>
            <div style="text-align: right;">
              <h2 style="margin:0; color:#282f81;">PROCEDURE SCHEDULE</h2>
              <p style="margin:2px 0; font-size:12px;">Print Date: ${today}</p>
            </div>
          </div>

          <div class="section-header">Patient Details</div>
          <div class="data-grid">
            <div class="data-item"><span class="label">Admission ID:</span> ${formData.admission_id}</div>
            <div class="data-item"><span class="label">Patient Name:</span> ${formData.patient_name}</div>
            <div class="data-item"><span class="label">Age / Gender:</span> ${formData.age} / ${formData.gender}</div>
            <div class="data-item"><span class="label">Location:</span> ${formData.department} | ${formData.ward_type} (Room: ${formData.room_no})</div>
          </div>

          <div class="section-header">Scheduled Procedures</div>
          <table>
            <thead>
              <tr>
                <th style="width: 15%">Date & Time</th>
                <th style="width: 20%">Procedure</th>
                <th style="width: 20%">Surgeon / Doctor</th>
                <th style="width: 15%">Location / OT</th>
                <th style="width: 20%">Priority & Notes</th>
                <th style="width: 10%">Status</th>
              </tr>
            </thead>
            <tbody>
              ${formData.procedures_array.map(p => `
                <tr>
                  <td>${new Date(p.surgery_date).toLocaleString()}</td>
                  <td style="font-weight:bold;">${p.proc_name} <br><span style="font-size:10px; color:#888;">(Est. ${p.duration || 'N/A'})</span></td>
                  <td>${p.performed_by_name}</td>
                  <td>${p.location}</td>
                  <td><span style="font-weight:bold; color:${p.priority === 'Immediate' ? '#e74c3c' : '#333'}">${p.priority}</span><br>${p.notes || '-'}</td>
                  <td>${p.status}</td>
                </tr>
              `).join('')}
              ${formData.procedures_array.length === 0 ? '<tr><td colspan="6" style="text-align:center;">No procedures scheduled.</td></tr>' : ''}
            </tbody>
          </table>

          <div class="footer">System Generated Report - Emedha HMS</div>
        </body>
      </html>
    `;
    newWindow.document.write(htmlContent);
    newWindow.document.close();
  };

  return (
    <div className="registration-container fade-in">
      
      {/* TOP SEARCH BOX */}
      {(screenAction === "edit" || screenAction === "view") && (
        <div className="patient-search">
          <label>Search Admission</label>
          <input
            type="text"
            value={admissionSearch}
            placeholder="Search by Admission ID, Patient ID, Name, or Mobile..."
            onChange={(e) => handleAdmissionSearch(e.target.value)}
            autoFocus
            autoComplete="off"
          />
          {showAdmissionDropdown && admissionResults.length > 0 && (
            <div className="patient-dropdown">
              {admissionResults.map((adm) => (
                <div key={adm.admission_id} className="patient-item" onClick={() => selectAdmission(adm)}>
                  <strong>{adm.admission_id}</strong> | {adm.patient_name} | {adm.patient_id} | Ph: {adm.mobile}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <form className="luxury-form">
        
        {/* 1. PATIENT & ADMISSION DETAILS */}
        <div className="form-section-header"><h3>1. Patient & Admission Details</h3></div>
        <div 
          className="form-grid" 
          style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "15px", alignItems: "flex-end" }}
        >
          {screenAction === "add" ? (
            <div className="input-box" style={{ position: "relative" }}>
              <label>Search Admission <span style={{color:'red'}}>*</span></label>
              <input 
                type="text" 
                value={admissionSearch} 
                onChange={(e) => handleAdmissionSearch(e.target.value)} 
                placeholder="Start typing to search..." 
                autoComplete="off"
              />
              {showAdmissionDropdown && admissionResults.length > 0 && (
                <div className="patient-dropdown" style={{ zIndex: 100, width: "100%", top: "100%" }}>
                  {admissionResults.map((adm) => (
                    <div key={adm.admission_id} className="patient-item" onClick={() => selectAdmission(adm)}>
                      <strong>{adm.admission_id}</strong> | {adm.patient_name} | {adm.patient_id}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="input-box read-only">
              <label>Admission ID</label>
              <input type="text" value={formData.admission_id} readOnly placeholder="Select from search above" />
            </div>
          )}

          <div className="input-box read-only">
            <label>Patient Name</label>
            <input type="text" value={formData.patient_name} readOnly />
          </div>
          <div className="input-box read-only">
            <label>Age / Gender</label>
            <input type="text" value={formData.age && formData.gender ? `${formData.age} / ${formData.gender}` : ""} readOnly />
          </div>
          <div className="input-box read-only">
            <label>Location</label>
            <input type="text" value={formData.department ? `${formData.department} - ${formData.ward_type} (Bed: ${formData.bed_no})` : ""} readOnly />
          </div>
        </div>

        {/* 2. SELECT PROCEDURE & DOCTOR */}
        <div className="form-section-header" style={{ marginTop: "20px" }}><h3>2. Select Procedure & Surgeon</h3></div>
        <div className="form-grid" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: "15px" }}>
          
          <div className="input-box" style={{ position: "relative" }}>
            <label>Search Procedure / Surgery <span style={{color:'red'}}>*</span></label>
            <input 
              type="text" 
              value={procedureSearch} 
              onChange={(e) => handleProcedureSearch(e.target.value)} 
              placeholder="Search by Procedure Name..." 
              disabled={isDisabled}
              autoComplete="off"
            />
            {showProcedureDropdown && procedureResults.length > 0 && (
              <div className="patient-dropdown" style={{ zIndex: 100, width: "100%", top: "100%" }}>
                {procedureResults.map((proc) => (
                  <div key={proc.proc_id} className="patient-item" onClick={() => selectProcedure(proc)}>
                    <strong>{proc.proc_name}</strong> | OT: {proc.surgery_location} | Est: {proc.duration}
                  </div>
                ))}
              </div>
            )}
            {selectedProcedure && (
               <p style={{ margin: "5px 0 0 0", fontSize: "12px", color: theme.primaryBlue, fontWeight: "bold" }}>
                 ✓ Selected: {selectedProcedure.proc_name}
               </p>
            )}
          </div>

          <div className="input-box" style={{ position: "relative" }}>
            <label>Surgeon / Performed By <span style={{color:'red'}}>*</span></label>
            <input 
              type="text" 
              value={doctorSearch} 
              onChange={(e) => handleDoctorSearch(e.target.value)} 
              placeholder="Search Doctor..." 
              disabled={isDisabled}
              autoComplete="off"
            />
            {showDoctorDropdown && doctorResults.length > 0 && (
              <div className="patient-dropdown" style={{ zIndex: 100, width: "100%", top: "100%" }}>
                {doctorResults.map((doc) => (
                  <div key={doc.doc_cd} className="patient-item" onClick={() => selectDoctor(doc)}>
                    {doc.doc_cd} | {doc.doctor_name} | {doc.department}
                  </div>
                ))}
              </div>
            )}
            {selectedDoctor && (
               <p style={{ margin: "5px 0 0 0", fontSize: "12px", color: theme.primaryBlue, fontWeight: "bold" }}>
                 ✓ Selected: {selectedDoctor.doctor_name}
               </p>
            )}
          </div>
        </div>

        {/* 3. SCHEDULE DETAILS */}
        <div className="form-section-header" style={{ marginTop: "20px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <h3 style={{ margin: 0 }}>3. Procedure Schedule</h3>
          <button 
            type="button" 
            onClick={addProcedure} 
            disabled={isDisabled || !formData.admission_id}
            style={{ 
              padding: '8px 16px', 
              backgroundColor: theme.primaryBlue, 
              color: 'white', 
              border: 'none', 
              borderRadius: '4px', 
              cursor: isDisabled || !formData.admission_id ? 'not-allowed' : 'pointer', 
              fontWeight: 'bold',
              opacity: isDisabled || !formData.admission_id ? 0.6 : 1
            }}>
            + Schedule Procedure
          </button>
        </div>

        <div 
          className="form-grid" 
          style={{ 
            backgroundColor: "#f9f9f9", padding: "15px", borderRadius: "8px", 
            display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "15px", alignItems: "flex-start" 
          }}
        >
          <div className="input-box">
            <label>Date & Time</label>
            <input 
              type="datetime-local" 
              value={currentProc.surgery_date} 
              onChange={(e) => setCurrentProc({...currentProc, surgery_date: e.target.value})} 
              disabled={isDisabled} 
            />
          </div>

          <div className="input-box">
            <label>Priority</label>
            <select 
              value={currentProc.priority} 
              disabled={isDisabled}
              onChange={(e) => setCurrentProc({ ...currentProc, priority: e.target.value })}
            >
              <option value="Routine">Routine</option>
              <option value="Urgent">Urgent</option>
              <option value="Immediate">Immediate</option>
            </select>
          </div>

          <div className="input-box">
            <label>OT / Location</label>
            <input 
              type="text" 
              value={currentProc.location} 
              onChange={(e) => setCurrentProc({...currentProc, location: e.target.value})} 
              disabled={isDisabled} 
              placeholder="e.g. OT-1" 
            />
          </div>

          <div className="input-box">
            <label>Est. Duration</label>
            <input 
              type="text" 
              value={currentProc.duration} 
              onChange={(e) => setCurrentProc({...currentProc, duration: e.target.value})} 
              disabled={isDisabled} 
              placeholder="e.g. 2 hours" 
            />
          </div>

          <div className="input-box">
            <label>Pre-op Notes / Instructions</label>
            <input 
              type="text" 
              value={currentProc.notes} 
              onChange={(e) => setCurrentProc({...currentProc, notes: e.target.value})} 
              disabled={isDisabled} 
              placeholder="Fasting required, etc." 
            />
          </div>

          <div className="input-box">
            <label>Status</label>
            <select 
              value={currentProc.status} 
              disabled={isDisabled}
              onChange={(e) => setCurrentProc({ ...currentProc, status: e.target.value })}
            >
              <option value="Scheduled">Scheduled</option>
              <option value="In Progress">In Progress</option>
              <option value="Completed">Completed</option>
              <option value="Cancelled">Cancelled</option>
            </select>
          </div>
        </div>

        {/* 4. PROCEDURES LIST TABLE */}
        {formData.procedures_array.length > 0 && (
          <div style={{ marginTop: '20px' }}>
            <h4 style={{ marginBottom: '10px', color: theme.primaryDark }}>Scheduled Procedures ({formData.procedures_array.length})</h4>
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '13px', minWidth: '900px' }}>
                <thead style={{ backgroundColor: theme.bgLight, borderBottom: '2px solid #ccc' }}>
                  <tr>
                    <th style={{ padding: '10px', width: '15%' }}>Date & Time</th>
                    <th style={{ padding: '10px', width: '20%' }}>Procedure & Location</th>
                    <th style={{ padding: '10px', width: '20%' }}>Surgeon</th>
                    <th style={{ padding: '10px', width: '20%' }}>Priority & Notes</th>
                    <th style={{ padding: '10px', width: '15%' }}>Status</th>
                    <th style={{ padding: '10px', textAlign: 'center', width: '10%' }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {formData.procedures_array.map((proc, index) => (
                    <tr key={index} style={{ borderBottom: '1px solid #ddd', backgroundColor: proc.id ? '#ffffff' : '#f4fcff' }}>
                      
                      <td style={{ padding: '10px' }}>
                        <input 
                          type="datetime-local" 
                          value={new Date(proc.surgery_date).toISOString().slice(0, 16)} 
                          onChange={(e) => {
                            const newArr = [...formData.procedures_array];
                            newArr[index].surgery_date = e.target.value;
                            setFormData({ ...formData, procedures_array: newArr });
                          }}
                          disabled={isDisabled}
                          style={{ border: isDisabled ? 'none' : '1px solid #ccc', background: 'transparent' }}
                        />
                      </td>

                      <td style={{ padding: '10px' }}>
                        <span style={{ color: theme.primaryBlue, fontWeight: 'bold' }}>{proc.proc_name}</span><br/>
                        <input 
                          type="text" 
                          value={proc.location || ''} 
                          onChange={(e) => {
                            const newArr = [...formData.procedures_array];
                            newArr[index].location = e.target.value;
                            setFormData({ ...formData, procedures_array: newArr });
                          }}
                          disabled={isDisabled}
                          style={{ border: isDisabled ? 'none' : '1px solid #ccc', background: 'transparent', fontSize: '11px', color: '#555', marginTop: '4px', width: '100%' }}
                          placeholder="Location / OT"
                        />
                      </td>

                      <td style={{ padding: '10px', fontWeight: 'bold' }}>
                        {proc.performed_by_name}
                      </td>

                      <td style={{ padding: '10px' }}>
                        <select 
                          value={proc.priority} 
                          disabled={isDisabled}
                          onChange={(e) => {
                            const newArr = [...formData.procedures_array];
                            newArr[index].priority = e.target.value;
                            setFormData({ ...formData, procedures_array: newArr });
                          }}
                          style={{ border: isDisabled ? 'none' : '1px solid #ccc', background: 'transparent', fontWeight: 'bold', color: proc.priority === 'Immediate' ? '#e74c3c' : '#333' }}
                        >
                          <option value="Routine">Routine</option>
                          <option value="Urgent">Urgent</option>
                          <option value="Immediate">Immediate</option>
                        </select>
                        <input 
                          type="text" 
                          value={proc.notes || ''} 
                          onChange={(e) => {
                            const newArr = [...formData.procedures_array];
                            newArr[index].notes = e.target.value;
                            setFormData({ ...formData, procedures_array: newArr });
                          }}
                          disabled={isDisabled}
                          style={{ border: isDisabled ? 'none' : '1px solid #ccc', background: 'transparent', width: '100%', marginTop: '4px' }}
                          placeholder="Notes..."
                        />
                      </td>
                      
                      <td style={{ padding: '10px' }}>
                        <select 
                          value={proc.status} 
                          disabled={isDisabled}
                          onChange={(e) => {
                            const newArr = [...formData.procedures_array];
                            newArr[index].status = e.target.value;
                            setFormData({ ...formData, procedures_array: newArr });
                          }}
                          style={{ border: isDisabled ? 'none' : '1px solid #ccc', background: 'transparent', fontWeight: 'bold' }}
                        >
                          <option value="Scheduled">Scheduled</option>
                          <option value="In Progress">In Progress</option>
                          <option value="Completed">Completed</option>
                          <option value="Cancelled">Cancelled</option>
                        </select>
                      </td>
                      
                      <td style={{ padding: '10px', textAlign: 'center' }}>
                        {!isDisabled && ( 
                          <button 
                            type="button" 
                            onClick={() => removeProcedure(index)} 
                            style={{ color: 'red', border: 'none', background: 'none', cursor: 'pointer', fontWeight: 'bold', padding: '5px' }}
                            title="Remove Record"
                          >
                            X
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </form>
    </div>
  );
};

export default IpdProcedures;